import {
  LibraryNameInput,
  LibraryNameInputRef,
} from '@components/LibraryNameInput/LibraryNameInput';
import { LibraryAndFolder } from '@components/MediaUploadBox/UploadButton';
import { UrlImportComponent } from '@components/MediaUploadBox/UrlImportComponent';
import { MobileModal } from '@components/MobileModal/MobileModal';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { IconButton, Tooltip } from '@mui/material';
import { BaseModal } from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import {
  LibraryDestination,
  LibrarySwitch,
} from '@pages/Search/components/SearchBar/components/LibrarySwitch/LibrarySwitch';
import { Library } from '@stores/Library/Library';
import { LibraryFolderProvider } from '@stores/Library/LibraryFolder';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import { UploadURLProvider } from '@stores/UploadURL/UploadURLStore';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LibraryCreateModal, LibraryCreateModalRef } from '../LibraryCreateModal';
import shell from '../LibraryModalShell.module.scss';
import css from './AddMediaModal.module.scss';
export type AddMediaModalRef = {
  open: () => void;
  close: () => void;
};

export const AddMediaModal = observer(
  forwardRef<AddMediaModalRef, { destination?: LibraryAndFolder; externalIsDragging?: boolean }>(
    ({ destination, externalIsDragging }, ref) => {
      const [open, setOpen] = useState(false);
      const isMobile = useIsMobile();
      const libraryManager = useLibraryManager();

      useModalRegistration('add-media', open);

      const onOpen = () => {
        setOpen(true);
      };
      const onClose = () => {
        setOpen(false);
      };

      useImperativeHandle(ref, () => ({
        open: onOpen,
        close: onClose,
      }));

      // User-chosen upload target. Null until the user picks; falls back to the
      // default (the library this modal was opened for, else the account default).
      const [selectedDestination, setSelectedDestination] = useState<LibraryDestination | null>(
        null,
      );
      const [newLibraryName, setNewLibraryName] = useState('');
      const nameInputRef = useRef<LibraryNameInputRef>(null);
      // "Advanced settings" opens the full create-library modal; on create it hands
      // the library back here so we can preselect it as the destination.
      const advancedRef = useRef<LibraryCreateModalRef>(null);
      // Mobile footer DOM node the upload button portals into (so it lives in the
      // MobileModal footer slot). Callback ref → state so the portal re-renders
      // once the node mounts.
      const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);

      // Reset the picker when the modal closes so it re-defaults on reopen.
      useEffect(() => {
        if (!open) {
          setSelectedDestination(null);
          setNewLibraryName('');
        }
      }, [open]);

      const defaultDestinationLibrary = destination?.libraryId
        ? libraryManager.libraryById(destination.libraryId)
        : libraryManager.defaultLibrary;

      const effectiveDestination: LibraryDestination | null =
        selectedDestination ??
        (defaultDestinationLibrary
          ? { kind: 'existing', library: defaultDestinationLibrary }
          : null);

      const createNewLibrary = effectiveDestination?.kind === 'new';
      const folderPath =
        effectiveDestination?.kind === 'existing' &&
        effectiveDestination.library.libraryId === destination?.libraryId
          ? destination.folderPath
          : undefined;

      const handleDestinationChange = (next: LibraryDestination) => {
        // No-op when re-picking the current target, so a typed new-library name
        // isn't wiped (and we don't double-fire tracking).
        const unchanged =
          next.kind === effectiveDestination?.kind &&
          (next.kind !== 'existing' ||
            next.library.libraryId ===
              (effectiveDestination as { library: { libraryId: string } }).library.libraryId);
        if (unchanged) return;

        setSelectedDestination(next);
        // Clear any typed name on every real switch, so it never lingers when the
        // target changes (and the inline input starts fresh on a new 'new' pick).
        setNewLibraryName('');
        trackingStore.uploadDestinationSelected({
          kind: next.kind,
          libraryId: next.kind === 'existing' ? next.library.libraryId : undefined,
        });
      };

      // "Advanced settings" created a library — preselect it as the destination.
      // The LibraryStore is already in the manager's observable map (createNewLibrary
      // inserts it), so the picker list updates itself; switching to 'existing' also
      // drops the inline-new mode. Look up the store by id (createNewLibrary returns
      // the raw Library, but the picker wants the store).
      const handleAdvancedCreated = (library: Library) => {
        const store = libraryManager.libraryById(library._id);
        if (store) handleDestinationChange({ kind: 'existing', library: store });
      };

      const openAdvancedSettings = () => {
        advancedRef.current?.open({
          defaultName: newLibraryName,
          onCreated: handleAdvancedCreated,
        });
      };

      // Focus the name input as soon as "New library" is chosen, so the user can
      // type the name right away (also pops the keyboard on mobile).
      useEffect(() => {
        if (createNewLibrary) nameInputRef.current?.focus();
      }, [createNewLibrary]);

      // Block proceeding when "New library" is chosen but unnamed (or whitespace-
      // only). Uses native form validation — surfaces the browser's required-field
      // bubble on the input and returns false so the caller aborts.
      const validateBeforeProceed = (): boolean => {
        if (!createNewLibrary) return true;
        if (newLibraryName.trim().length > 0) return true;
        const input = nameInputRef.current?.getInputRef();
        if (input) {
          input.setCustomValidity('Please name your new library.');
          input.reportValidity();
        }
        nameInputRef.current?.focus();
        return false;
      };

      // The library backing the folder context. A brand-new library doesn't exist
      // yet, so we still provide the default as context; the upload flow creates the
      // real one on confirm (createNewLibrary).
      const providerLibrary =
        effectiveDestination?.kind === 'existing'
          ? effectiveDestination.library
          : defaultDestinationLibrary;

      // Shared header (the destination picker) and body, rendered in the desktop
      // BaseModal or the mobile bottom-sheet MobileModal so both match their
      // platform's modal chrome — same as the upload confirm screens.
      const header = (
        <div className={css.destinationRow}>
          <span className={css.destinationLabel}>Add to</span>
          <LibrarySwitch
            mode="destination"
            destination={effectiveDestination ?? undefined}
            onDestinationChange={handleDestinationChange}
          />
        </div>
      );

      const body = providerLibrary && (
        <>
          {createNewLibrary && (
            <LibraryNameInput
              ref={nameInputRef}
              id="add-media-new-library-name"
              placeholder="Name your new library"
              required
              onValueChange={(v) => {
                setNewLibraryName(v);
                // Clear the native validity message as soon as the user types.
                nameInputRef.current?.getInputRef()?.setCustomValidity('');
              }}
              endAdornment={
                <Tooltip title="Advanced settings">
                  <IconButton
                    className={css.advancedSettingsButton}
                    size="small"
                    aria-label="Advanced settings"
                    onClick={openAdvancedSettings}
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
          )}
          {/* UploadURLProvider sits ABOVE the keyed LibraryFolderProvider on purpose.
              Keying on the library id forces a fresh LibraryFolder — and thus correct
              per-library dedup — when the user switches the target, but that remount used to
              take the URL store down with it, emptying the URL field and (before the scrape
              cache) discarding an in-flight channel scrape. UploadURLStore doesn't depend on
              LibraryFolder, so hoisting it out of the keyed subtree keeps the typed URLs and
              their entries intact across a destination change. */}
          <UploadURLProvider>
            <LibraryFolderProvider key={providerLibrary.libraryId} arguments={[providerLibrary]}>
              <UrlImportComponent
                closeModal={onClose}
                externalIsDragging={externalIsDragging}
                autoFocus={open}
                createNewLibrary={createNewLibrary}
                newLibraryName={createNewLibrary ? newLibraryName : undefined}
                footerPortalTarget={isMobile ? footerSlot : undefined}
                validateBeforeProceed={validateBeforeProceed}
                folderPath={folderPath}
              />
            </LibraryFolderProvider>
          </UploadURLProvider>
        </>
      );

      return (
        <>
          {isMobile ? (
            <MobileModal
              open={open}
              onClose={onClose}
              title={header}
              footer={<div className={css.mobileFooterSlot} ref={setFooterSlot} />}
            >
              <div className={css.mobileBody}>{body}</div>
            </MobileModal>
          ) : (
            <BaseModal open={open} className={shell.modal} onClose={onClose} headerContent={header}>
              {body}
            </BaseModal>
          )}
          {/* Advanced settings → full create-library modal; on create it preselects
              the new library here (see handleAdvancedCreated). */}
          <LibraryCreateModal ref={advancedRef} />
        </>
      );
    },
  ),
);
